import { lazy, Suspense } from 'react';

import { ListSkeleton } from '@/shared/components/skeletons';
import type { AttachmentSourcePickerProps } from '@/shared/registry/types';
import { Skeleton } from '@/shared/ui/skeleton';

/*
 * What the module contributes to other places (side panel, group and profile tabs, the attachment picker) — loaded on
 * first use so the disk's code stays out of the main bundle.
 */

const FilePanelLazy = lazy(() => import('./panel/FilePanel'));
const GroupDiskTabLazy = lazy(async () => ({ default: (await import('./components/tabs')).GroupDiskTab }));
const ProfileFilesTabLazy = lazy(async () => ({
  default: (await import('./components/tabs')).ProfileFilesTab,
}));
const PickerLazy = lazy(async () => ({
  default: (await import('./dialogs/DiskAttachmentPicker')).DiskAttachmentPicker,
}));

function PanelFallback() {
  return (
    <div className="space-y-4 p-6" aria-busy>
      <div className="flex items-center gap-3">
        <Skeleton className="size-12 rounded-lg" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-5 w-1/2" />
          <Skeleton className="h-3 w-1/3" />
        </div>
      </div>
      <Skeleton className="h-72 w-full rounded-xl" />
    </div>
  );
}

export function FilePanelEntry({ id }: { id: string }) {
  return (
    <Suspense fallback={<PanelFallback />}>
      <FilePanelLazy id={id} />
    </Suspense>
  );
}

export function GroupDiskTabEntry({ groupId }: { groupId: string }) {
  return (
    <Suspense fallback={<ListSkeleton rows={5} avatar={false} />}>
      <GroupDiskTabLazy groupId={groupId} />
    </Suspense>
  );
}

export function ProfileFilesTabEntry({ userId }: { userId: string }) {
  return (
    <Suspense fallback={<ListSkeleton rows={4} avatar={false} />}>
      <ProfileFilesTabLazy userId={userId} />
    </Suspense>
  );
}

export function DiskAttachmentPickerEntry(props: AttachmentSourcePickerProps) {
  return (
    <Suspense fallback={null}>
      <PickerLazy {...props} />
    </Suspense>
  );
}
